import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';

// Checkmark icon component
const CheckIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" className="flex-shrink-0 mt-[2px]">
    <path d="M20 6L9 17L4 12" stroke="#0D0C07" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// Sad face icon component (for pain points)
const SadFaceIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" className="flex-shrink-0 mt-[2px]">
    <path d="M16 16C16 16 14.5 14 12 14C9.5 14 8 16 8 16M9 9H9.01M15 9H15.01M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z" stroke="#0D0C07" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

function AboutUs() {
  const navigate = useNavigate();
  const { t } = useTranslation('about');

  return (
    <div className="min-h-safe-screen bg-[#fff]">
      {/* Block 1 — Who we are & why 9 Meals exists */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6">
          
          <img
            src="/assets/about-1.png"
            alt="About 9 Meals"
            className="mb-10 rounded-[12px] w-full max-h-96 object-contain"
          />
          
          <h1 className="text-[24px] font-bold leading-[32px] tracking-[-0.1024px] text-[#0D0C07] mb-6">
            {t('section1.title')}
          </h1>

          <p className="text-[16px] leading-[28px] mb-6 text-[#0d0c07]">
            {t('section1.intro')}
            <span className="font-semibold italic"> {t('section1.introHighlight')}</span>
          </p>

          <p className="text-[16px] leading-[28px] mb-4 text-[#4d4b42]">
            {t('section1.struggling')}
          </p>

          <ul className="space-y-3 text-[16px] leading-[28px] mb-6 text-[#4d4b42]">
            <li className="flex items-start gap-2">
              <SadFaceIcon />
              <span>{t('section1.painPoints.0')}</span>
            </li>
            <li className="flex items-start gap-2">
              <SadFaceIcon />
              <span><Trans i18nKey="section1.painPoints.1" ns="about" components={{ 1: <span className="italic" /> }} /></span>
            </li>
            <li className="flex items-start gap-2">
              <SadFaceIcon />
              <span>{t('section1.painPoints.2')}</span>
            </li>
            <li className="flex items-start gap-2">
              <SadFaceIcon />
              <span>{t('section1.painPoints.3')}</span>
            </li>
            <li className="flex items-start gap-2">
              <SadFaceIcon />
              <span>{t('section1.painPoints.4')}</span>
            </li>
            <li className="flex items-start gap-2">
              <SadFaceIcon />
              <span>{t('section1.painPoints.5')}</span>
            </li>
          </ul>

          <p className="text-[16px] leading-[28px] mb-6 text-[#4d4b42]">
            {t('section1.tried')}
          </p>

          <p className="text-[16px] leading-[28px] text-[#0d0c07]">
            <Trans i18nKey="section1.built" ns="about" components={{ 1: <strong className="font-bold" /> }} />
          </p>
        </div>
      </section>

      {/* Block 2 — What 9 Meals actually does */}
      <section className="py-16 bg-[#f3f3f2]">
        <div className="max-w-4xl mx-auto px-6">
          <img
            src="/assets/about-2.png"
            alt="What 9 Meals does"
            className="mb-10 rounded-[12px] w-full max-h-96 object-contain"
          />
          
          <h2 className="text-[24px] font-bold leading-[32px] tracking-[-0.1024px] text-[#0D0C07] mb-6">
            {t('section2.title')}
          </h2>

          <p className="text-[16px] leading-[28px] mb-6 text-[#0d0c07]">
            <Trans i18nKey="section2.intro" ns="about" components={{ 1: <strong className="font-semibold" /> }} />
          </p>

          <p className="text-[16px] leading-[28px] mb-4 text-[#4d4b42]">
            {t('section2.everyRun')}
          </p>

          <ul className="space-y-3 text-[16px] leading-[28px] mb-6 text-[#4d4b42]">
            <li className="flex items-start gap-2">
              <CheckIcon />
              <span><Trans i18nKey="section2.features.0" ns="about" components={{ 1: <strong className="font-semibold text-[#0d0c07]" /> }} /></span>
            </li>
            <li className="flex items-start gap-2">
              <CheckIcon />
              <span><Trans i18nKey="section2.features.1" ns="about" components={{ 1: <strong className="font-semibold text-[#0d0c07]" /> }} /></span>
            </li>
            <li className="flex items-start gap-2">
              <CheckIcon />
              <span><Trans i18nKey="section2.features.2" ns="about" components={{ 1: <strong className="font-semibold text-[#0d0c07]" /> }} /></span>
            </li>
          </ul>

          <p className="text-[16px] leading-[28px] mb-4 text-[#4d4b42]">
            {t('section2.setPreferences')}
          </p>

          <ul className="space-y-3 text-[16px] leading-[28px] mb-6 text-[#4d4b42]">
            <li className="flex items-start gap-2">
              <CheckIcon />
              <span><Trans i18nKey="section2.recipeFeatures.0" ns="about" components={{ 1: <strong className="font-semibold text-[#0d0c07]" /> }} /></span>
            </li>
            <li className="flex items-start gap-2">
              <CheckIcon />
              <span>{t('section2.recipeFeatures.1')}</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckIcon />
              <span>{t('section2.recipeFeatures.2')}</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckIcon />
              <span>{t('section2.recipeFeatures.3')}</span>
            </li>
          </ul>

          <p className="text-[16px] leading-[28px] mb-6 text-[#4d4b42]">
            <Trans i18nKey="section2.flexibility" ns="about" components={{ 1: <strong className="font-semibold text-[#0d0c07]" /> }} />
          </p>

          <p className="text-[16px] leading-[28px] text-[#006600]">
            <Trans i18nKey="section2.summary" ns="about" components={{ 1: <strong className="font-semibold" />, 2: <strong className="font-semibold" />, 3: <strong className="font-semibold" /> }} />
          </p>
        </div>
      </section>

      {/* Block 3 — How it helps families */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6">
          <img
            src="/assets/about-3.png"
            alt="How 9 Meals helps families"
            className="mb-10 rounded-[12px] w-full max-h-96 object-contain"
          />
          
          <h2 className="text-[24px] font-bold leading-[32px] tracking-[-0.1024px] text-[#0D0C07] mb-6">
            {t('section3.title')}
          </h2>

          <p className="text-[16px] leading-[28px] mb-4 text-[#4d4b42]">
            {t('section3.intro')}
          </p>

          <ul className="space-y-3 text-[16px] leading-[28px] mb-6 text-[#4d4b42]">
            <li className="flex items-start gap-2">
              <CheckIcon />
              <span><Trans i18nKey="section3.routine.0" ns="about" components={{ 1: <strong className="font-semibold text-[#0d0c07]" /> }} /></span>
            </li>
            <li className="flex items-start gap-2">
              <CheckIcon />
              <span><Trans i18nKey="section3.routine.1" ns="about" components={{ 1: <strong className="font-semibold text-[#0d0c07]" /> }} /></span>
            </li>
            <li className="flex items-start gap-2">
              <CheckIcon />
              <span><Trans i18nKey="section3.routine.2" ns="about" components={{ 1: <strong className="font-semibold text-[#0d0c07]" /> }} /></span>
            </li>
            <li className="flex items-start gap-2">
              <CheckIcon />
              <span>{t('section3.routine.3')}</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckIcon />
              <span><Trans i18nKey="section3.routine.4" ns="about" components={{ 1: <strong className="font-semibold text-[#0d0c07]" /> }} /></span>
            </li>
          </ul>

          <p className="text-[16px] leading-[28px] mb-6 text-[#4d4b42]">
            <Trans i18nKey="section3.learning" ns="about" components={{ 1: <strong className="font-semibold text-[#0d0c07]" /> }} />
          </p>

          <p className="text-[16px] leading-[28px] mb-4 text-[#4d4b42]">
            {t('section3.resultsIntro')}
          </p>

          <ul className="space-y-3 text-[16px] leading-[28px] mb-6 text-[#4d4b42]">
            <li className="flex items-start gap-2">
              <CheckIcon />
              <span>{t('section3.results.0')}</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckIcon />
              <span>{t('section3.results.1')}</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckIcon />
              <span>{t('section3.results.2')}</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckIcon />
              <span>{t('section3.results.3')}</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckIcon />
              <span>{t('section3.results.4')}</span>
            </li>
          </ul>

          <p className="text-[16px] leading-[28px] text-[#0d0c07]">
            <Trans i18nKey="section3.mission" ns="about" components={{ 1: <strong className="font-bold text-[#006600]" /> }} />
          </p>
        </div>
      </section>

      {/* Block 4 — Freemium model */}
      <section className="py-16 bg-[#f3f3f2]">
        <div className="max-w-4xl mx-auto px-6">
          <img
            src="/assets/about-4.png"
            alt="Our freemium model"
            className="mb-10 rounded-[12px] w-full max-h-96 object-contain"
          />
          
          <h2 className="text-[24px] font-bold leading-[32px] tracking-[-0.1024px] text-[#0D0C07] mb-6">
            {t('section4.title')}
          </h2>

          <p className="text-[16px] leading-[28px] mb-4 text-[#4d4b42]">
            {t('section4.wantToStay')}
          </p>

          <ul className="space-y-3 text-[16px] leading-[28px] mb-6 text-[#4d4b42]">
            <li className="flex items-start gap-2">
              <CheckIcon />
              <span>{t('section4.values.0')}</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckIcon />
              <span>{t('section4.values.1')}</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckIcon />
              <span>{t('section4.values.2')}</span>
            </li>
          </ul>

          <p className="text-[16px] leading-[28px] mb-4 text-[#4d4b42]">
            <Trans i18nKey="section4.freemiumIntro" ns="about" components={{ 1: <strong className="font-semibold text-[#0d0c07]" /> }} />
          </p>

          <ul className="space-y-3 text-[16px] leading-[28px] mb-6 text-[#4d4b42]">
            <li className="flex items-start gap-2">
              <CheckIcon />
              <span>
                <Trans i18nKey="section4.plans.0" ns="about" components={{ 1: <strong className="font-semibold text-[#0d0c07]" />, 2: <strong className="font-semibold text-[#0d0c07]" /> }} />
              </span>
            </li>
            <li className="flex items-start gap-2">
              <CheckIcon />
              <span>
                <Trans i18nKey="section4.plans.1" ns="about" components={{ 1: <strong className="font-semibold text-[#0d0c07]" />, 2: <strong className="font-semibold text-[#0d0c07]" /> }} />
              </span>
            </li>
          </ul>

          <p className="text-[16px] leading-[28px] mb-4 text-[#4d4b42]">
            {t('section4.coversIntro')}
          </p>

          <ul className="space-y-3 text-[16px] leading-[28px] mb-8 text-[#4d4b42]">
            <li className="flex items-start gap-2">
              <CheckIcon />
              <span>{t('section4.covers.0')}</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckIcon />
              <span>{t('section4.covers.1')}</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckIcon />
              <span>{t('section4.covers.2')}</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckIcon />
              <span>{t('section4.covers.3')}</span>
            </li>
          </ul>

          <p className="text-[16px] leading-[28px] mb-6 text-[#4d4b42]">
            {t('section4.noDarkPatterns')}
          </p>

          <blockquote className="border-l-4 border-[#ffcb00] pl-6 py-4 mb-8 bg-white rounded-r-[12px] shadow-[0px_1px_2px_0px_rgba(35,33,22,0.04),_0px_6px_12px_0px_rgba(83,81,69,0.04)]">
            <p className="italic text-[16px] leading-[28px] text-[#56554d]">
              {t('section4.quote')}
            </p>
          </blockquote>

          <p className="text-[16px] leading-[28px] mb-10 text-[#0d0c07]">
            <Trans i18nKey="section4.closing" ns="about" components={{ 1: <strong className="font-bold text-[#006600]" /> }} />
          </p>

          <div className="text-center pb-24">
            <button
              className="
                inline-flex justify-center items-center gap-1
                h-[56px] px-[40px] py-[14px]
                rounded-[32px]
                border-[1px] border-[linear-gradient(200deg,_#090_0%,_#060_100%)]
                bg-[#090]
                text-[#FFF] text-center
                text-[14px] font-medium leading-[28px]
                transition-colors
                md:hover:border-[0.5px] md:hover:border-[#060]
                md:hover:bg-[#060]
                bg-[linear-gradient(200deg,_#090_0%,_#060_100%)]
                shadow-[0_2px_16px_0_rgba(0,0,0,0.12)_inset,_0_0_32px_0_rgba(255,255,255,0.4)_inset,_0_0px_0px_2px_rgba(0,102,0,0.4)]
              "
              onClick={() => navigate('/wizard')}
            >
              <span>{t('section4.cta')}</span>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M11.3816 5.54742C11.702 5.22734 12.2089 5.20753 12.5525 5.48785L12.6189 5.54742L18.4519 11.3814C18.7936 11.7231 18.7936 12.277 18.4519 12.6187L12.6189 18.4517L12.5525 18.5123C12.2088 18.7925 11.7019 18.7721 11.3816 18.4517C11.0399 18.11 11.0399 17.5561 11.3816 17.2144L15.7214 12.8746H6.16675C5.6835 12.8746 5.29175 12.4828 5.29175 11.9996C5.29192 11.5165 5.68361 11.1246 6.16675 11.1246H15.7205L11.3816 6.7857L11.321 6.7193C11.0406 6.37562 11.0612 5.86782 11.3816 5.54742Z"
                  fill="white"
                />
              </svg>
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}

export default AboutUs;
